import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import ReviewerPersonaEditor from './personas/ReviewerPersonaEditor';
import SandboxPersonaFilter from './SandboxPersonaFilter';

// Persona purpose options
const PERSONA_PURPOSES = [
  { value: 'therapist', label: 'Therapist', description: 'Delivers therapy to clients' },
  { value: 'onboarding_assistant', label: 'Onboarding Assistant', description: 'Guides users through onboarding' },
  { value: 'therapy_reviewer', label: 'Therapy Reviewer', description: 'Evaluates therapy sessions' },
  { value: 'report_generator', label: 'Report Generator', description: 'Creates summaries and reports' },
  { value: 'assessment_guide', label: 'Assessment Guide', description: 'Guides through assessments' },
  { value: 'crisis_support', label: 'Crisis Support', description: 'Crisis intervention support' },
  { value: 'caregiver', label: 'Caregiver (Gen 2)', description: 'Gen 2 transdiagnostic caregiver' }
];

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const PROVIDER_LABELS = { vertex_ai: 'Vertex AI', gemini: 'Gemini API', openai: 'OpenAI', anthropic: 'Anthropic' };
const EMPTY_LLM_PROVIDER = { provider: 'default', model: '', temperature: null, maxTokens: null };

// The model a persona runs on is chosen from the model list in Admin → LLM
// Configuration; "default" follows the platform primary model. The server
// refuses a model that is not active there.
const toLlmProviderPayload = (llm) => {
  const provider = (llm && llm.provider) || 'default';
  const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
  return {
    provider,
    model: provider === 'default' ? null : ((llm && llm.model) || null),
    temperature: num(llm && llm.temperature),
    maxTokens: num(llm && llm.maxTokens),
    customSettings: (llm && llm.customSettings) || {}
  };
};

const TherapistPromptEditor = ({ token }) => {
  const [personas, setPersonas] = useState([]);
  const [filteredPersonas, setFilteredPersonas] = useState([]);
  const [selectedPersonaKey, setSelectedPersonaKey] = useState('');
  const [availableModules, setAvailableModules] = useState([]);
  const [llmConfig, setLlmConfig] = useState(null);
  const [purposeFilter, setPurposeFilter] = useState(''); // Empty = show all
  const [voiceCatalog, setVoiceCatalog] = useState([]);
  const [avatarLibrary, setAvatarLibrary] = useState([]);
  const [representations, setRepresentations] = useState([]);
  const [formData, setFormData] = useState({
    key: '',
    name: '',
    system_prompt: '',
    personaPurpose: 'therapist',
    description: '',
    clientDescription: '',
    defaultVoiceCatalogId: '',
    avatarId: '',
    allowedRepresentationKeys: [],
    toolAssignments: [],
    llmProvider: { ...EMPTY_LLM_PROVIDER },
    promptVersion: 1,
    gen2Config: {
      enabled: false,
      defaultModules: [],
      reasoningConfig: {
        deepThinkingTriggers: {
          firstSession: true,
          significantGapDays: 7,
          newDomainConcern: true,
          measurementChange: true,
          clinicalDecisionPoint: true
        },
        consultationTriggers: {
          periodicReviewMessages: 10,
          domainQuestionDetected: true,
          formulationChange: true,
          presentationShift: true
        },
        formulationContextMaxTokens: 500
      },
      therapeuticOrientation: ''
    }
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cloneSource, setCloneSource] = useState(null); // Full persona object for reviewer cloning

  // Fetch personas on load
  const fetchPersonas = async () => {
    if (!token) return;
    setIsLoading(true);
    setError('');
    try {
      const data = await apiService.getAdminTherapistPersonas(token);
      setPersonas(data || []);
    } catch (err) {
      setError('Failed to load therapist personas.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPersonas();
    // Fetch specialist modules and LLM config for Gen 2 section
    const fetchGen2Data = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/admin/specialist-modules?active=true`, {
          headers: { 'x-auth-token': token }
        });
        if (res.ok) setAvailableModules(await res.json());
      } catch (err) { console.error('Error fetching specialist modules:', err); }
      try {
        const data = await apiService.getLLMConfiguration(token);
        setLlmConfig(data);
      } catch (err) { console.error('Error fetching LLM config:', err); }
    };
    if (token) fetchGen2Data();

    // Load voice catalog + avatar library for the voice/avatar pickers.
    const fetchVoiceAvatar = async () => {
      try {
        const [vres, ares] = await Promise.all([
          fetch(`${API_BASE_URL}/admin/voice-library/voices`, { headers: { 'x-auth-token': token } }),
          fetch(`${API_BASE_URL}/admin/voice-library/avatars`, { headers: { 'x-auth-token': token } })
        ]);
        if (vres.ok) {
          const data = await vres.json();
          setVoiceCatalog(data?.voices || []);
        }
        if (ares.ok) {
          const data = await ares.json();
          setAvatarLibrary(data?.avatars || []);
        }
      } catch (err) { console.error('Error fetching voice/avatar catalog:', err); }
    };
    if (token) fetchVoiceAvatar();

    // Load active persona representations for the persona's allowed-rep whitelist.
    const fetchRepresentations = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/admin/persona-representations`, {
          headers: { 'x-auth-token': token }
        });
        if (res.ok) setRepresentations(await res.json());
      } catch (err) { console.error('Error fetching representations:', err); }
    };
    if (token) fetchRepresentations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Filter personas when filter changes
  useEffect(() => {
    if (!purposeFilter) {
      setFilteredPersonas(personas);
    } else {
      setFilteredPersonas(personas.filter(p => (p.personaPurpose || 'therapist') === purposeFilter));
    }
  }, [personas, purposeFilter]);

  // Count personas by purpose
  const purposeCounts = personas.reduce((acc, p) => {
    const purpose = p.personaPurpose || 'therapist';
    acc[purpose] = (acc[purpose] || 0) + 1;
    return acc;
  }, {});

  const handleSelectPersona = (key) => {
    setSelectedPersonaKey(key);
    setCloneSource(null);
    const selected = personas.find(p => p.key === key);
    if (selected) {
      setFormData({
        key: selected.key,
        name: selected.name,
        system_prompt: selected.system_prompt || '',
        personaPurpose: selected.personaPurpose || 'therapist',
        description: selected.description || '',
        clientDescription: selected.clientDescription || '',
        defaultVoiceCatalogId: selected.defaultVoiceCatalogId || '',
        avatarId: selected.avatarId || '',
        allowedRepresentationKeys: selected.allowedRepresentationKeys || [],
        toolAssignments: selected.toolAssignments || [],
        llmProvider: { ...EMPTY_LLM_PROVIDER, ...(selected.llmProvider || {}), model: (selected.llmProvider && selected.llmProvider.model) || '' },
        promptVersion: selected.promptVersion || 1,
        gen2Config: selected.gen2Config || {
          enabled: false,
          defaultModules: [],
          reasoningConfig: {
            deepThinkingTriggers: { firstSession: true, significantGapDays: 7, newDomainConcern: true, measurementChange: true, clinicalDecisionPoint: true },
            consultationTriggers: { periodicReviewMessages: 10, domainQuestionDetected: true, formulationChange: true, presentationShift: true },
            formulationContextMaxTokens: 500
          },
          therapeuticOrientation: ''
        }
      });
      setIsEditing(true);
      setError('');
      setSuccess('');
    } else {
      handleNewPersona();
    }
  };

  const handleNewPersona = () => {
    setSelectedPersonaKey('');
    setCloneSource(null);
    setFormData({
      key: '',
      name: '',
      system_prompt: '',
      personaPurpose: purposeFilter || 'therapist', // Default to current filter or therapist
      description: '',
      clientDescription: '',
      defaultVoiceCatalogId: '',
      avatarId: '',
      allowedRepresentationKeys: [],
      toolAssignments: [],
      llmProvider: { ...EMPTY_LLM_PROVIDER },
      promptVersion: 1
    });
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  const handleClonePersona = () => {
    if (!selectedPersonaKey) return;
    const toolCount = formData.toolAssignments?.length || 0;

    // For therapy_reviewer personas, capture the full persona object so
    // ReviewerPersonaEditor can initialize with all reviewer-specific fields
    if (formData.personaPurpose === 'therapy_reviewer') {
      const sourcePersona = personas.find(p => p.key === selectedPersonaKey);
      if (sourcePersona) {
        setCloneSource({
          ...sourcePersona,
          _id: undefined, // Remove _id so it saves as new
          key: sourcePersona.key + '-clone',
          name: sourcePersona.name + ' (Clone)',
        });
      }
    }

    setFormData({
      ...formData,
      key: formData.key + '_clone',
      name: formData.name + ' (Clone)',
    });
    setSelectedPersonaKey('');
    setIsEditing(false);
    setError('');
    setSuccess(
      `Change the key and description, then click Save Reviewer to save the clone.` +
      (toolCount > 0 ? ` ${toolCount} tool assignment(s) will be copied.` : '')
    );
  };

  const handleCloneFromSandbox = async (sandboxPersona) => {
    const payload = {
      key: sandboxPersona.key,
      name: sandboxPersona.name,
      system_prompt: sandboxPersona.system_prompt || '',
      personaPurpose: 'therapist',
      description: sandboxPersona.description || ''
    };
    if (sandboxPersona.toolAssignments && sandboxPersona.toolAssignments.length > 0) {
      payload.toolAssignments = sandboxPersona.toolAssignments;
    }
    const response = await apiService.addTherapistPersona(payload, token);
    setPersonas(prev => [...prev, response.persona]);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle save from ReviewerPersonaEditor
  const handleReviewerSave = async (reviewerData) => {
    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      let response;
      if (isEditing) {
        response = await apiService.updateTherapistPersona(selectedPersonaKey, reviewerData, token);
        setSuccess(response.msg || 'Reviewer persona updated successfully!');
        setPersonas(personas.map(p => p.key === selectedPersonaKey ? { ...p, ...response.persona } : p));
      } else {
        response = await apiService.addTherapistPersona(reviewerData, token);
        setSuccess(response.msg || 'Reviewer persona added successfully!');
        setPersonas([...personas, response.persona]);
        setCloneSource(null);
        handleSelectPersona(response.persona.key);
      }
    } catch (err) {
      setError(err.response?.data?.msg || err.message || 'Failed to save reviewer persona.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    if (!formData.key || !formData.name || !formData.system_prompt) {
      setError('Key, Name, and System Prompt are required.');
      setIsLoading(false);
      return;
    }

    try {
      let response;
      const payload = {
        name: formData.name,
        system_prompt: formData.system_prompt,
        personaPurpose: formData.personaPurpose,
        description: formData.description,
        clientDescription: formData.clientDescription || '',
        defaultVoiceCatalogId: formData.defaultVoiceCatalogId || null,
        avatarId: formData.avatarId || null,
        allowedRepresentationKeys: formData.allowedRepresentationKeys || [],
        gen2Config: formData.gen2Config,
        llmProvider: toLlmProviderPayload(formData.llmProvider),
        promptVersion: Number(formData.promptVersion) || 1
      };

      if (isEditing) {
        response = await apiService.updateTherapistPersona(selectedPersonaKey, payload, token);
        setSuccess(response.msg || 'Therapist persona updated successfully!');
        // Update local state
        setPersonas(personas.map(p => p.key === selectedPersonaKey ? { ...p, ...response.persona } : p));
      } else {
        payload.key = formData.key;
        // Include tool assignments when creating (e.g., from clone)
        if (formData.toolAssignments && formData.toolAssignments.length > 0) {
          payload.toolAssignments = formData.toolAssignments;
        }
        response = await apiService.addTherapistPersona(payload, token);
        setSuccess(response.msg || 'Therapist persona added successfully!');
        setPersonas([...personas, response.persona]);
        handleSelectPersona(response.persona.key);
      }

    } catch (err) {
      setError(err.response?.data?.msg || err.message || 'Failed to save therapist persona.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPersonaKey || !window.confirm(`Are you sure you want to delete therapist persona "${formData.name}"?`)) {
      return;
    }
    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      await apiService.deleteTherapistPersona(selectedPersonaKey, token);
      setSuccess('Therapist persona deleted successfully!');
      setPersonas(personas.filter(p => p.key !== selectedPersonaKey));
      handleNewPersona();
    } catch (err) {
      setError(err.response?.data?.msg || err.message || 'Failed to delete therapist persona.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Export / Download ──────────────────────────────────────────────────

  const stripDbFields = (persona) => {
    const { _id, __v, createdAt, updatedAt, isActive, ...exportData } = persona;
    return exportData;
  };

  const downloadJson = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportSelected = () => {
    if (!selectedPersonaKey) return;
    const selected = personas.find(p => p.key === selectedPersonaKey);
    if (!selected) return;
    const exportData = stripDbFields(selected);
    downloadJson(exportData, `${selected.key}-therapist-persona.json`);
  };

  const handleExportAll = () => {
    const toExport = filteredPersonas.length ? filteredPersonas : personas;
    if (!toExport.length) return;
    const exportData = toExport.map(stripDbFields);
    const date = new Date().toISOString().slice(0, 10);
    const suffix = purposeFilter ? `-${purposeFilter}` : '';
    downloadJson(exportData, `therapist-personas${suffix}-export-${date}.json`);
  };

  const getPurposeLabel = (purpose) => {
    const found = PERSONA_PURPOSES.find(p => p.value === purpose);
    return found ? found.label : purpose;
  };

  const getPurposeBadgeColor = (purpose) => {
    const colors = {
      therapist: '#4caf50',
      onboarding_assistant: '#2196f3',
      therapy_reviewer: '#9c27b0',
      report_generator: '#ff9800',
      assessment_guide: '#00bcd4',
      crisis_support: '#f44336'
    };
    return colors[purpose] || '#757575';
  };

  return (
    <div>
      <h3>Therapist Prompt Editor</h3>

      {/* Purpose Filter */}
      <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
        <label htmlFor="purpose-filter" style={{ marginRight: '10px', fontWeight: 'bold' }}>
          Filter by Type:
        </label>
        <select
          id="purpose-filter"
          value={purposeFilter}
          onChange={(e) => {
            const newFilter = e.target.value;
            setPurposeFilter(newFilter);
            // When switching filter with no persona selected, update the form purpose
            // so the correct editor (generic vs reviewer) shows immediately
            if (!isEditing && newFilter) {
              setFormData(prev => ({ ...prev, personaPurpose: newFilter }));
            }
          }}
          style={{ padding: '5px 10px', marginRight: '15px' }}
        >
          <option value="">All Types ({personas.length})</option>
          {PERSONA_PURPOSES.map(p => (
            <option key={p.value} value={p.value}>
              {p.label} ({purposeCounts[p.value] || 0})
            </option>
          ))}
        </select>
        <small style={{ color: '#666' }}>
          Showing {filteredPersonas.length} of {personas.length} personas
        </small>
      </div>

      {/* Sandbox Persona Browser */}
      <SandboxPersonaFilter token={token} personaType="therapist" onCloneSandbox={handleCloneFromSandbox} />

      {/* Persona Selection */}
      <div style={{ marginBottom: '15px' }}>
        <label htmlFor="therapist-persona-select">Select Persona: </label>
        <select
          id="therapist-persona-select"
          value={selectedPersonaKey}
          onChange={(e) => handleSelectPersona(e.target.value)}
          disabled={isLoading}
          style={{ minWidth: '300px' }}
        >
          <option value="">-- Select Existing --</option>
          {filteredPersonas.map(p => (
            <option key={p.key} value={p.key}>
              {p.name} ({p.key}) [{getPurposeLabel(p.personaPurpose || 'therapist')}]
            </option>
          ))}
        </select>
        <button onClick={handleNewPersona} style={{ marginLeft: '10px' }} disabled={isLoading}>
          Create New Persona
        </button>
        {selectedPersonaKey && (
          <button
            onClick={handleExportSelected}
            disabled={isLoading}
            style={{ marginLeft: '10px', backgroundColor: '#607d8b', color: 'white', padding: '4px 12px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            title="Export selected persona as JSON"
          >
            Export Selected
          </button>
        )}
        <button
          onClick={handleExportAll}
          disabled={isLoading || !filteredPersonas.length}
          style={{ marginLeft: '10px', backgroundColor: '#607d8b', color: 'white', padding: '4px 12px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          title={purposeFilter ? `Export all ${getPurposeLabel(purposeFilter)} personas` : 'Export all therapist personas'}
        >
          {purposeFilter ? `Export All ${getPurposeLabel(purposeFilter)}` : 'Export All'}
        </button>
      </div>

      {/* Reviewer Persona Editor (specialized UI) */}
      {formData.personaPurpose === 'therapy_reviewer' ? (
        <div>
          {error && <p style={{ color: 'red' }}>{error}</p>}
          {success && <p style={{ color: 'green' }}>{success}</p>}
          <ReviewerPersonaEditor
            key={selectedPersonaKey || (cloneSource ? cloneSource.key : '__new__')}
            persona={isEditing ? personas.find(p => p.key === selectedPersonaKey) : cloneSource}
            onSave={handleReviewerSave}
            onCancel={handleNewPersona}
            isLoading={isLoading}
            token={token}
          />
          {isEditing && (
            <div style={{ marginTop: '10px' }}>
              <button
                type="button"
                onClick={handleClonePersona}
                disabled={isLoading}
                style={{ marginLeft: '10px', backgroundColor: '#2196f3', color: 'white', padding: '6px 16px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                Clone Persona
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={isLoading}
                style={{ marginLeft: '10px', backgroundColor: 'red', color: 'white', padding: '6px 16px', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                Delete Persona
              </button>
            </div>
          )}
        </div>
      ) : (
      /* Generic Persona Edit Form */
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '10px' }}>
          <label htmlFor="therapist-key">Key: </label>
          <input
            type="text"
            id="therapist-key"
            name="key"
            value={formData.key}
            onChange={handleChange}
            disabled={isLoading || isEditing}
            required
            placeholder="e.g., generalist_v2"
            style={{ width: '300px' }}
          />
          {isEditing && <small style={{ marginLeft: '10px' }}>(Cannot change key)</small>}
        </div>

        <div style={{ marginBottom: '10px' }}>
          <label htmlFor="therapist-name">Name: </label>
          <input
            type="text"
            id="therapist-name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            disabled={isLoading}
            required
            placeholder="e.g., General AI Therapist V2"
            style={{ width: '300px' }}
          />
        </div>

        {/* Persona Purpose Selector */}
        <div style={{ marginBottom: '10px' }}>
          <label htmlFor="therapist-purpose">Persona Type: </label>
          <select
            id="therapist-purpose"
            name="personaPurpose"
            value={formData.personaPurpose}
            onChange={handleChange}
            disabled={isLoading}
            style={{ padding: '5px 10px' }}
          >
            {PERSONA_PURPOSES.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          <span
            style={{
              marginLeft: '10px',
              padding: '2px 8px',
              borderRadius: '4px',
              backgroundColor: getPurposeBadgeColor(formData.personaPurpose),
              color: 'white',
              fontSize: '12px'
            }}
          >
            {getPurposeLabel(formData.personaPurpose)}
          </span>
          <br />
          <small style={{ color: '#666', marginLeft: '100px' }}>
            {PERSONA_PURPOSES.find(p => p.value === formData.personaPurpose)?.description}
          </small>
        </div>

        {/* Description Field */}
        <div style={{ marginBottom: '10px' }}>
          <label htmlFor="therapist-description">Description: </label>
          <input
            type="text"
            id="therapist-description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            disabled={isLoading}
            placeholder="Brief description of this persona"
            style={{ width: '90%', display: 'block' }}
          />
        </div>

        {/* Client-facing description, voice, avatar */}
        <div style={{ marginBottom: '15px', padding: '12px', border: '1px solid #cfd8dc', borderRadius: '6px', backgroundColor: '#f7fafc' }}>
          <h4 style={{ margin: '0 0 8px 0' }}>Client-facing presentation</h4>
          <div style={{ marginBottom: '8px' }}>
            <label htmlFor="persona-clientDescription">Client description: </label>
            <textarea
              id="persona-clientDescription"
              name="clientDescription"
              value={formData.clientDescription}
              onChange={handleChange}
              disabled={isLoading}
              rows="2"
              placeholder="Short, plain-language blurb shown to clients in the voice/persona picker."
              style={{ width: '90%', display: 'block' }}
            />
          </div>
          <div style={{ marginBottom: '8px' }}>
            <label htmlFor="persona-voice">Default voice: </label>
            <select
              id="persona-voice"
              name="defaultVoiceCatalogId"
              value={formData.defaultVoiceCatalogId || ''}
              onChange={handleChange}
              disabled={isLoading}
              style={{ minWidth: '300px' }}
            >
              <option value="">— no default (use cascade) —</option>
              {voiceCatalog.filter(v => v.isActive !== false).map(v => (
                <option key={v._id} value={v._id}>
                  {v.name} ({v.tags?.gender || '—'}{v.tags?.accent ? `, ${v.tags.accent}` : ''})
                </option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: '4px' }}>
            <label htmlFor="persona-avatar">Avatar: </label>
            <select
              id="persona-avatar"
              name="avatarId"
              value={formData.avatarId || ''}
              onChange={handleChange}
              disabled={isLoading}
              style={{ minWidth: '300px' }}
            >
              <option value="">— no avatar —</option>
              {avatarLibrary.filter(a => a.isActive !== false).map(a => (
                <option key={a._id} value={a._id}>{a.name} ({a.style})</option>
              ))}
            </select>
            {(() => {
              const selectedAvatar = avatarLibrary.find(a => a._id === formData.avatarId);
              return selectedAvatar?.imageUrl ? (
                <img
                  src={selectedAvatar.imageUrl}
                  alt=""
                  style={{ width: 48, height: 48, borderRadius: '50%', marginLeft: 10, verticalAlign: 'middle' }}
                />
              ) : null;
            })()}
          </div>
          {representations.length > 0 && (
            <div style={{ marginTop: '10px' }}>
              <label style={{ display: 'block', marginBottom: '4px' }}>
                Allowed persona representations (avatar+voice bundles):
              </label>
              <select
                multiple
                value={formData.allowedRepresentationKeys || []}
                onChange={(e) => {
                  const opts = Array.from(e.target.selectedOptions, o => o.value);
                  setFormData(prev => ({ ...prev, allowedRepresentationKeys: opts }));
                }}
                disabled={isLoading}
                size={Math.min(6, representations.length + 1)}
                style={{ minWidth: '300px' }}
              >
                {representations.filter(r => r.isActive !== false).map(r => (
                  <option key={r.key} value={r.key}>
                    {r.name} ({r.tags?.gender || '—'}{r.tags?.accent ? `, ${r.tags.accent}` : ''})
                  </option>
                ))}
              </select>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                Empty = all platform representations are available for this persona.
                Hold Ctrl/Cmd to select multiple. Service groups and therapists can further narrow.
              </div>
            </div>
          )}
        </div>

        <div style={{ marginBottom: '10px' }}>
          <label htmlFor="therapist-system_prompt">System Prompt: </label>
          <textarea
            id="therapist-system_prompt"
            name="system_prompt"
            value={formData.system_prompt}
            onChange={handleChange}
            disabled={isLoading}
            required
            rows="12"
            style={{ width: '90%', display: 'block' }}
            placeholder="Detailed instructions for the AI Therapist's core behavior, personality, and techniques."
          />
        </div>

        {(formData.personaPurpose === 'therapist' || formData.personaPurpose === 'caregiver') && (
          <div style={{ marginBottom: '10px' }}>
            <label htmlFor="therapist-prompt-version">Prompt version: </label>
            <select
              id="therapist-prompt-version"
              value={String(formData.promptVersion || 1)}
              onChange={(e) => setFormData(prev => ({ ...prev, promptVersion: Number(e.target.value) }))}
              disabled={isLoading}
            >
              <option value="1">1 (full prompt; techniques written in the text)</option>
              <option value="2">2 (v2 prompt; the platform adds approach cards)</option>
            </select>
          </div>
        )}

        {/* Model — chosen from the model list in Admin → LLM Configuration */}
        <div style={{ marginBottom: '15px', padding: '15px', border: '1px solid #ccc', borderRadius: '8px' }}>
          <h4 style={{ margin: '0 0 6px 0' }}>Model</h4>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
            Models are added, retired and deactivated in Admin → LLM Configuration. Default follows the platform primary model
            {llmConfig && llmConfig.primaryModel ? ' (currently ' + llmConfig.primaryModel + ')' : ''}. A saved change reaches live chat within about a minute.
          </div>
          <label htmlFor="therapist-llm-provider">Provider: </label>
          <select
            id="therapist-llm-provider"
            value={(formData.llmProvider && formData.llmProvider.provider) || 'default'}
            onChange={(e) => {
              const provider = e.target.value;
              setFormData(prev => ({ ...prev, llmProvider: { ...(prev.llmProvider || EMPTY_LLM_PROVIDER), provider, model: '' } }));
            }}
            disabled={isLoading}
          >
            <option value="default">Default (platform primary)</option>
            {[...new Set(((llmConfig && llmConfig.availableModels) || []).filter(m => m.isActive !== false && !m.isDeprecated).map(m => m.provider))].map(p => (
              <option key={p} value={p}>{PROVIDER_LABELS[p] || p}</option>
            ))}
          </select>
          {' '}
          <label htmlFor="therapist-llm-model">Model: </label>
          <select
            id="therapist-llm-model"
            value={(formData.llmProvider && formData.llmProvider.model) || ''}
            onChange={(e) => {
              const model = e.target.value;
              setFormData(prev => ({ ...prev, llmProvider: { ...(prev.llmProvider || EMPTY_LLM_PROVIDER), model } }));
            }}
            disabled={isLoading || !formData.llmProvider || formData.llmProvider.provider === 'default'}
          >
            <option value="">Provider default</option>
            {((llmConfig && llmConfig.availableModels) || [])
              .filter(m => m.isActive !== false && !m.isDeprecated && formData.llmProvider && m.provider === formData.llmProvider.provider)
              .map(m => (
                <option key={m.provider + '-' + m.id} value={m.id}>{m.displayName || m.id}</option>
              ))}
            {formData.llmProvider && formData.llmProvider.model && !((llmConfig && llmConfig.availableModels) || []).some(m => m.id === formData.llmProvider.model && m.provider === formData.llmProvider.provider && m.isActive !== false && !m.isDeprecated) && (
              <option value={formData.llmProvider.model}>{formData.llmProvider.model} (not active in LLM Configuration)</option>
            )}
          </select>
          {formData.llmProvider && formData.llmProvider.model && llmConfig && !((llmConfig.availableModels || []).some(m => m.id === formData.llmProvider.model && m.provider === formData.llmProvider.provider && m.isActive !== false && !m.isDeprecated)) && (
            <div style={{ color: '#b00020', fontSize: '12px', marginTop: '6px' }}>
              {formData.llmProvider.model} is not an active model in LLM Configuration. Saving is refused until it is added or reactivated there.
            </div>
          )}
        </div>

        {/* Gen 2 Configuration Section — shown for therapist and caregiver personas */}
        {(formData.personaPurpose === 'therapist' || formData.personaPurpose === 'caregiver') && (
          <div style={{ marginBottom: '15px', padding: '15px', border: '2px solid #1976d2', borderRadius: '8px', backgroundColor: '#f5f9ff' }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#1976d2' }}>Gen 2 Transdiagnostic Configuration</h4>
            <div style={{ marginBottom: '10px' }}>
              <label>
                <input
                  type="checkbox"
                  checked={formData.gen2Config?.enabled || false}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    gen2Config: { ...prev.gen2Config, enabled: e.target.checked }
                  }))}
                  disabled={isLoading}
                />
                {' '}Enable Gen 2 Reasoning Pipeline
              </label>
              <br />
              <small style={{ color: '#666' }}>
                When enabled, this persona will use deep thinking and specialist consultation instead of direct LLM calls.
              </small>
            </div>

            {formData.gen2Config?.enabled && (
              <>
                <div style={{ marginBottom: '10px' }}>
                  <label>Therapeutic Orientation: </label>
                  <input
                    type="text"
                    value={formData.gen2Config?.therapeuticOrientation || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      gen2Config: { ...prev.gen2Config, therapeuticOrientation: e.target.value }
                    }))}
                    disabled={isLoading}
                    placeholder="e.g., transdiagnostic, integrative, developmental"
                    style={{ width: '300px' }}
                  />
                </div>

                <div style={{ marginBottom: '10px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Default Specialist Modules:</label>
                  {availableModules.length === 0 ? (
                    <small style={{ color: '#666' }}>No specialist modules available. Create them via the Specialist Modules page first.</small>
                  ) : (
                    <div style={{ border: '1px solid #ccc', borderRadius: '4px', padding: '8px', maxHeight: '300px', overflowY: 'auto' }}>
                      {availableModules.map(mod => (
                        <label key={mod.key} style={{ display: 'block', marginBottom: '4px' }}>
                          <input
                            type="checkbox"
                            checked={(formData.gen2Config?.defaultModules || []).includes(mod.key)}
                            onChange={(e) => {
                              const current = formData.gen2Config?.defaultModules || [];
                              const updated = e.target.checked
                                ? [...current, mod.key]
                                : current.filter(k => k !== mod.key);
                              setFormData(prev => ({
                                ...prev,
                                gen2Config: { ...prev.gen2Config, defaultModules: updated }
                              }));
                            }}
                            disabled={isLoading}
                          />
                          {' '}{mod.name} <small style={{ color: '#888' }}>({mod.key})</small>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '10px' }}>
                  <label>Formulation Context Max Tokens: </label>
                  <input
                    type="number"
                    value={formData.gen2Config?.reasoningConfig?.formulationContextMaxTokens || 500}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      gen2Config: {
                        ...prev.gen2Config,
                        reasoningConfig: {
                          ...prev.gen2Config?.reasoningConfig,
                          formulationContextMaxTokens: parseInt(e.target.value) || 500
                        }
                      }
                    }))}
                    disabled={isLoading}
                    min="100" max="2000"
                    style={{ width: '100px' }}
                  />
                </div>

                <fieldset style={{ marginBottom: '10px', padding: '10px', borderRadius: '4px' }}>
                  <legend>Deep Thinking Triggers</legend>
                  <label style={{ display: 'block', marginBottom: '5px' }}>
                    <input type="checkbox"
                      checked={formData.gen2Config?.reasoningConfig?.deepThinkingTriggers?.firstSession ?? true}
                      onChange={(e) => setFormData(prev => ({
                        ...prev, gen2Config: { ...prev.gen2Config, reasoningConfig: { ...prev.gen2Config?.reasoningConfig, deepThinkingTriggers: { ...prev.gen2Config?.reasoningConfig?.deepThinkingTriggers, firstSession: e.target.checked } } }
                      }))} disabled={isLoading}
                    /> First session
                  </label>
                  <label style={{ display: 'block', marginBottom: '5px' }}>
                    <input type="checkbox"
                      checked={formData.gen2Config?.reasoningConfig?.deepThinkingTriggers?.newDomainConcern ?? true}
                      onChange={(e) => setFormData(prev => ({
                        ...prev, gen2Config: { ...prev.gen2Config, reasoningConfig: { ...prev.gen2Config?.reasoningConfig, deepThinkingTriggers: { ...prev.gen2Config?.reasoningConfig?.deepThinkingTriggers, newDomainConcern: e.target.checked } } }
                      }))} disabled={isLoading}
                    /> New domain concern
                  </label>
                  <label style={{ display: 'block', marginBottom: '5px' }}>
                    <input type="checkbox"
                      checked={formData.gen2Config?.reasoningConfig?.deepThinkingTriggers?.measurementChange ?? true}
                      onChange={(e) => setFormData(prev => ({
                        ...prev, gen2Config: { ...prev.gen2Config, reasoningConfig: { ...prev.gen2Config?.reasoningConfig, deepThinkingTriggers: { ...prev.gen2Config?.reasoningConfig?.deepThinkingTriggers, measurementChange: e.target.checked } } }
                      }))} disabled={isLoading}
                    /> Measurement change
                  </label>
                  <label style={{ display: 'block', marginBottom: '5px' }}>
                    <input type="checkbox"
                      checked={formData.gen2Config?.reasoningConfig?.deepThinkingTriggers?.clinicalDecisionPoint ?? true}
                      onChange={(e) => setFormData(prev => ({
                        ...prev, gen2Config: { ...prev.gen2Config, reasoningConfig: { ...prev.gen2Config?.reasoningConfig, deepThinkingTriggers: { ...prev.gen2Config?.reasoningConfig?.deepThinkingTriggers, clinicalDecisionPoint: e.target.checked } } }
                      }))} disabled={isLoading}
                    /> Clinical decision point
                  </label>
                  <label style={{ display: 'inline-block', marginRight: '10px' }}>
                    Significant gap (days):
                    <input type="number" min="1"
                      value={formData.gen2Config?.reasoningConfig?.deepThinkingTriggers?.significantGapDays || 7}
                      onChange={(e) => setFormData(prev => ({
                        ...prev, gen2Config: { ...prev.gen2Config, reasoningConfig: { ...prev.gen2Config?.reasoningConfig, deepThinkingTriggers: { ...prev.gen2Config?.reasoningConfig?.deepThinkingTriggers, significantGapDays: parseInt(e.target.value) || 7 } } }
                      }))} disabled={isLoading}
                      style={{ width: '60px', marginLeft: '5px' }}
                    />
                  </label>
                </fieldset>

                <fieldset style={{ marginBottom: '10px', padding: '10px', borderRadius: '4px' }}>
                  <legend>Consultation Triggers</legend>
                  <label style={{ display: 'inline-block', marginRight: '15px', marginBottom: '5px' }}>
                    Periodic review every
                    <input type="number" min="1"
                      value={formData.gen2Config?.reasoningConfig?.consultationTriggers?.periodicReviewMessages || 10}
                      onChange={(e) => setFormData(prev => ({
                        ...prev, gen2Config: { ...prev.gen2Config, reasoningConfig: { ...prev.gen2Config?.reasoningConfig, consultationTriggers: { ...prev.gen2Config?.reasoningConfig?.consultationTriggers, periodicReviewMessages: parseInt(e.target.value) || 10 } } }
                      }))} disabled={isLoading}
                      style={{ width: '60px', margin: '0 5px' }}
                    />
                    client messages
                  </label>
                  <br />
                  <label style={{ display: 'block', marginBottom: '5px' }}>
                    <input type="checkbox"
                      checked={formData.gen2Config?.reasoningConfig?.consultationTriggers?.domainQuestionDetected ?? true}
                      onChange={(e) => setFormData(prev => ({
                        ...prev, gen2Config: { ...prev.gen2Config, reasoningConfig: { ...prev.gen2Config?.reasoningConfig, consultationTriggers: { ...prev.gen2Config?.reasoningConfig?.consultationTriggers, domainQuestionDetected: e.target.checked } } }
                      }))} disabled={isLoading}
                    /> Domain question detected
                  </label>
                  <label style={{ display: 'block', marginBottom: '5px' }}>
                    <input type="checkbox"
                      checked={formData.gen2Config?.reasoningConfig?.consultationTriggers?.formulationChange ?? true}
                      onChange={(e) => setFormData(prev => ({
                        ...prev, gen2Config: { ...prev.gen2Config, reasoningConfig: { ...prev.gen2Config?.reasoningConfig, consultationTriggers: { ...prev.gen2Config?.reasoningConfig?.consultationTriggers, formulationChange: e.target.checked } } }
                      }))} disabled={isLoading}
                    /> Formulation change
                  </label>
                  <label style={{ display: 'block', marginBottom: '5px' }}>
                    <input type="checkbox"
                      checked={formData.gen2Config?.reasoningConfig?.consultationTriggers?.presentationShift ?? true}
                      onChange={(e) => setFormData(prev => ({
                        ...prev, gen2Config: { ...prev.gen2Config, reasoningConfig: { ...prev.gen2Config?.reasoningConfig, consultationTriggers: { ...prev.gen2Config?.reasoningConfig?.consultationTriggers, presentationShift: e.target.checked } } }
                      }))} disabled={isLoading}
                    /> Presentation shift
                  </label>
                </fieldset>
              </>
            )}
          </div>
        )}

        {error && <p style={{ color: 'red' }}>{error}</p>}
        {success && <p style={{ color: 'green' }}>{success}</p>}

        <div style={{ marginTop: '15px' }}>
          <button type="submit" disabled={isLoading}>
            {isLoading ? 'Saving...' : (isEditing ? 'Update Persona' : 'Add Persona')}
          </button>
          {isEditing && (
            <button
              type="button"
              onClick={handleClonePersona}
              disabled={isLoading}
              style={{ marginLeft: '10px', backgroundColor: '#2196f3', color: 'white' }}
            >
              Clone Persona
            </button>
          )}
          {isEditing && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={isLoading}
              style={{ marginLeft: '10px', backgroundColor: 'red', color: 'white' }}
            >
              Delete Persona
            </button>
          )}
        </div>
      </form>
      )}
    </div>
  );
};

export default TherapistPromptEditor;
